import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShare2, FiCheck, FiCopy, FiLink, FiX, FiTwitter, FiFacebook, FiMail, FiMessageSquare, FiImage } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { generateSEOFriendlyURL, generatePlatformSpecificText } from '../utils/shareUtils';
import ImageGenerator from './image-generation/ImageGenerator';

/**
 * ShareButton - 重構後的分享按鈕組件
 * 職責：分享功能、圖片生成入口
 */
const ShareButton = ({ 
  language, 
  calculatedDays, 
  relationshipLabel, 
  anniversaryDate, 
  anniversaryType, 
  detailedTime, 
  shareUrl, 
  quote 
}) => {
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showImageGenerator, setShowImageGenerator] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [generatedShareUrl, setGeneratedShareUrl] = useState('');
  const popoverRef = useRef(null);

  // Close popover on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setShowShareOptions(false);
      }
    };

    if (showShareOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareOptions]);

  // Generate share URL
  useEffect(() => {
    if (anniversaryDate) {
      const url = generateSEOFriendlyURL({ anniversaryDate, anniversaryType, language });
      setGeneratedShareUrl(url);
    } else {
      setGeneratedShareUrl(window.location.href);
    }
  }, [anniversaryDate, anniversaryType, language, shareUrl]);

  const handleShare = async () => {
    const shareTexts = generatePlatformSpecificText({ language, calculatedDays, relationshipLabel, detailedTime });

    if (navigator.share) {
      try {
        await navigator.share({
          title: language === 'zh' ? '我們的愛情時光' : 'Our Love Journey',
          text: shareTexts.base,
          url: generatedShareUrl
        });
      } catch (error) {
        console.log('Sharing failed', error);
        setShowShareOptions(true);
      }
    } else {
      setShowShareOptions(true);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedShareUrl).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const handleCopyText = () => {
    const shareTexts = generatePlatformSpecificText({ language, calculatedDays, relationshipLabel, detailedTime });
    navigator.clipboard.writeText(shareTexts.base + ' ' + generatedShareUrl).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const shareToSocialMedia = (platform) => {
    const shareTexts = generatePlatformSpecificText({ language, calculatedDays, relationshipLabel, detailedTime });
    let finalShareUrl;
    const encodedUrl = encodeURIComponent(generatedShareUrl);

    switch (platform) {
      case 'twitter':
        finalShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTexts.social)}&url=${encodedUrl}`;
        break;
      case 'facebook':
        finalShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodeURIComponent(shareTexts.social)}`;
        break;
      case 'email':
        finalShareUrl = `mailto:?subject=${encodeURIComponent(language === 'zh' ? '我們的愛情時光' : 'Our Love Journey')}&body=${encodeURIComponent(shareTexts.email + ' ' + generatedShareUrl)}`;
        break;
      case 'sms':
        finalShareUrl = `sms:?&body=${encodeURIComponent(shareTexts.sms + ' ' + generatedShareUrl)}`;
        break;
      default:
        return;
    }

    window.open(finalShareUrl, '_blank', 'noopener, noreferrer');
  };

  const previewText = generatePlatformSpecificText({ language, calculatedDays, relationshipLabel, detailedTime }).base;

  return (
    <>
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
        {/* Generate Image Button */}
        <motion.button
          className="flex items-center justify-center gap-1 sm:gap-2 bg-love-500 hover:bg-love-600 text-white font-medium py-2 px-2 sm:px-4 rounded-full transition-all shadow-sm text-xs sm:text-sm"
          onClick={() => setShowImageGenerator(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label={language === 'zh' ? '生成圖片' : 'Generate Image'}
        >
          <SafeIcon icon={FiImage} className="text-base sm:text-lg" />
          <span>{language === 'zh' ? '生成圖片' : 'Generate'}</span>
        </motion.button>

        {/* Share Button */}
        <div className="relative">
          <motion.button
            className="flex items-center justify-center gap-1 sm:gap-2 bg-love-100 dark:bg-love-900 hover:bg-love-200 dark:hover:bg-love-800 text-love-700 dark:text-love-300 font-medium py-2 px-2 sm:px-4 rounded-full transition-colors text-xs sm:text-sm"
            onClick={handleShare}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label={language === 'zh' ? '分享' : 'Share'}
          >
            <SafeIcon icon={FiShare2} className="text-base sm:text-lg" />
            <span>{language === 'zh' ? '分享' : 'Share'}</span>
          </motion.button>

          {/* Share Options Popover */}
          <AnimatePresence>
            {showShareOptions && (
              <motion.div
                ref={popoverRef}
                className="absolute top-full mt-3 right-0 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 w-80 sm:w-96 z-50 border border-love-100 dark:border-gray-700"
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ type: "spring", damping: 20 }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {language === 'zh' ? '分享你們的愛情時光' : 'Share your love journey'}
                  </div>
                  <button
                    onClick={() => setShowShareOptions(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="text-lg" />
                  </button>
                </div>

                <div className="bg-love-50 dark:bg-love-900/20 p-3 rounded-lg mb-4 border border-love-100 dark:border-love-900/50">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {language === 'zh' ? '預覽' : 'Preview'}:
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                    {previewText}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">
                    {generatedShareUrl}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <motion.button
                    onClick={() => shareToSocialMedia('facebook')}
                    className="flex items-center justify-center gap-2 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <SafeIcon icon={FiFacebook} className="text-lg" />
                    <span>Facebook</span>
                  </motion.button>

                  <motion.button
                    onClick={() => shareToSocialMedia('twitter')}
                    className="flex items-center justify-center gap-2 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <SafeIcon icon={FiTwitter} className="text-lg" />
                    <span>Twitter</span>
                  </motion.button>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <motion.button
                    onClick={() => shareToSocialMedia('email')}
                    className="flex items-center justify-center gap-2 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <SafeIcon icon={FiMail} className="text-lg" />
                    <span>Email</span>
                  </motion.button>

                  <motion.button
                    onClick={() => shareToSocialMedia('sms')}
                    className="flex items-center justify-center gap-2 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <SafeIcon icon={FiMessageSquare} className="text-lg" />
                    <span>SMS</span>
                  </motion.button>
                </div>

                <div className="flex flex-col gap-2 mb-4">
                  <motion.button
                    onClick={handleCopyText}
                    className="flex items-center gap-2 bg-love-50 dark:bg-love-900/50 hover:bg-love-100 dark:hover:bg-love-900 text-left p-2 rounded-md transition-colors"
                    whileHover={{ x: 3 }}
                  >
                    <SafeIcon icon={FiCopy} className="text-love-600 dark:text-love-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {language === 'zh' ? '複製文字和連結' : 'Copy text & link'}
                    </span>
                  </motion.button>

                  <motion.button
                    onClick={handleCopyLink}
                    className="flex items-center gap-2 bg-love-50 dark:bg-love-900/50 hover:bg-love-100 dark:hover:bg-love-900 text-left p-2 rounded-md transition-colors"
                    whileHover={{ x: 3 }}
                  >
                    <SafeIcon icon={FiLink} className="text-love-600 dark:text-love-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {language === 'zh' ? '僅複製連結' : 'Copy link only'}
                    </span>
                  </motion.button>

                  {copySuccess && (
                    <div className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 mt-1 ml-2">
                      <SafeIcon icon={FiCheck} />
                      <span>{language === 'zh' ? '已複製!' : 'Copied!'}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Image Generator Modal */}
      <ImageGenerator
        language={language}
        calculatedDays={calculatedDays}
        relationshipLabel={relationshipLabel}
        detailedTime={detailedTime}
        anniversaryDate={anniversaryDate}
        anniversaryType={anniversaryType}
        quote={quote}
        isOpen={showImageGenerator}
        onClose={() => setShowImageGenerator(false)}
      />
    </>
  );
};

export default ShareButton;